import { useRef, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';

export const useThetaMonitor = () => {
  const thetaRef = useRef<number>(0);
  const lastScrollRef = useRef<number>(useAppStore.getState().scrollOffset);
  const lastTimeRef = useRef<number>(performance.now());
  const velocityHistoryRef = useRef<number[]>([]);
  const lastVelocityUpdateRef = useRef<number>(0);
  const { setScrollOffset, setVelocity } = useAppStore();

  useEffect(() => {
    const updateMetrics = () => {
      const now = performance.now();
      const deltaTime = now - lastTimeRef.current;
      
      // Only update if enough time has passed for stable calculation
      if (deltaTime > 16) { // ~60fps
        const currentScroll = useAppStore.getState().scrollOffset;

        // Scroll programmatique (indicateur) : velocity pilotée par l'animation, pas par le delta
        if (useAppStore.getState().isIndicatorScrolling) {
          velocityHistoryRef.current = [];
          lastScrollRef.current = currentScroll;
          lastTimeRef.current = now;
          return;
        }
        
        // Calculate accurate velocity
        const deltaScroll = currentScroll - lastScrollRef.current;
        const instantVelocity = Math.abs(deltaScroll) / (deltaTime / 1000); // units per second
        
        // Maintain velocity history for smoothing
        velocityHistoryRef.current.push(instantVelocity);
        if (velocityHistoryRef.current.length > 5) {
          velocityHistoryRef.current.shift();
        }
        
        // Use average of recent velocities for stability
        const smoothedVelocity = velocityHistoryRef.current.reduce((a, b) => a + b, 0) / velocityHistoryRef.current.length;
        
        // Update velocity if significant change or apply natural decay
        const currentVelocity = useAppStore.getState().velocity;
        const timeSinceLastUpdate = now - lastVelocityUpdateRef.current;
        
        const VELOCITY_THRESHOLD = 0.0001;

        if (smoothedVelocity > VELOCITY_THRESHOLD) {
          setVelocity(smoothedVelocity);
          lastVelocityUpdateRef.current = now;
        } else if (timeSinceLastUpdate > 100 && currentVelocity > 0) {
          // Apply natural decay after 100ms of no significant movement
          const decayedVelocity = currentVelocity * Math.exp(-timeSinceLastUpdate / 300);
          const finalVelocity = Math.max(decayedVelocity, 0);

          if (finalVelocity < VELOCITY_THRESHOLD) {
            setVelocity(0);
          } else {
            setVelocity(finalVelocity);
          }
          lastVelocityUpdateRef.current = now;
        } else if (currentVelocity === 0 && smoothedVelocity <= VELOCITY_THRESHOLD) {
          setVelocity(0);
        }
        
        // Update theta based on scroll position
        thetaRef.current = currentScroll * 2 * Math.PI; // Full rotation per unit
        
        lastScrollRef.current = currentScroll;
        lastTimeRef.current = now;
      }
    };

    const interval = setInterval(updateMetrics, 16); // ~60fps updates
    return () => clearInterval(interval);
  }, [setScrollOffset, setVelocity]);

  return thetaRef;
};
